// Pure helpers that turn a FormSchema + values into the things the renderer
// and the hook need: the visible field list, the validation result, and the
// submit payload. No React here so this is trivially unit-testable and can be
// reused by any future renderer (a native app, a server-side pre-check).

import { evaluate, isBlank } from "./conditions";
import { runRule } from "./validators";

/** @typedef {import("./types").FormSchema} FormSchema */
/** @typedef {import("./types").FieldSchema} FieldSchema */

/**
 * Every field in the schema, regardless of visibility, in render order.
 * @param {FormSchema} schema
 * @returns {FieldSchema[]}
 */
export const allFields = (schema) =>
  (schema?.sections || []).flatMap((s) => s.fields || []);

/**
 * Fields that are currently visible — a hidden section hides its fields too.
 * Validation and the submit payload both key off this, so a conditional field
 * that isn't on screen can never block the CTA or leak a stale value upstream.
 *
 * @param {FormSchema} schema
 * @param {Record<string, *>} values
 * @returns {FieldSchema[]}
 */
export const visibleFields = (schema, values) =>
  (schema?.sections || [])
    .filter((s) => evaluate(s.visibleWhen, values))
    .flatMap((s) => (s.fields || []).filter((f) => evaluate(f.visibleWhen, values)));

/**
 * Is this field required right now? `required: true` is the static case;
 * `requiredWhen` makes it depend on other answers (corporate-only fields).
 * @param {FieldSchema} field
 * @param {Record<string, *>} values
 */
export const isRequired = (field, values) =>
  field.required === true || (field.requiredWhen ? evaluate(field.requiredWhen, values) : false);

/**
 * Validate one visible field: requiredness first, then its rules in order.
 * @returns {string} error message, or "" when valid
 */
export const validateField = (field, values) => {
  const value = values?.[field.name];
  if (isRequired(field, values) && isBlank(value)) {
    return field.requiredMessage || `${field.label || "This field"} is required`;
  }
  for (const rule of field.rules || []) {
    const message = runRule(rule, value, values);
    if (message) return message;
  }
  return "";
};

/**
 * Validate the whole form.
 * @returns {{ errors: Record<string, string>, isValid: boolean }}
 */
export const validateSchema = (schema, values) => {
  /** @type {Record<string, string>} */
  const errors = {};
  for (const field of visibleFields(schema, values)) {
    const message = validateField(field, values);
    if (message) errors[field.name] = message;
  }
  return { errors, isValid: Object.keys(errors).length === 0 };
};

/**
 * Starting values for a schema: config defaults, then anything already known
 * (a resumed case, an OCR extraction) layered on top.
 * @param {FormSchema} schema
 * @param {Record<string, *>} [initial]
 */
export const initialValues = (schema, initial = {}) => {
  /** @type {Record<string, *>} */
  const values = {};
  for (const field of allFields(schema)) {
    values[field.name] = field.defaultValue !== undefined ? field.defaultValue : "";
  }
  for (const [key, value] of Object.entries(initial || {})) {
    if (value !== undefined && value !== null) values[key] = value;
  }
  return values;
};

/**
 * The object to send upstream: visible fields only, so a hidden corporate
 * field never ships a stale value that a carrier would reject.
 */
export const submitPayload = (schema, values) => {
  /** @type {Record<string, *>} */
  const payload = {};
  for (const field of visibleFields(schema, values)) {
    payload[field.name] = values[field.name];
  }
  return payload;
};

/**
 * The first required, visible field still empty — what "scroll down, there's
 * more to fill in" should actually take you to.
 *
 * @param {FormSchema} schema
 * @param {Record<string, *>} values
 * @returns {string} field name, or "" when the step is complete
 */
export const firstIncomplete = (schema, values) => {
  const field = visibleFields(schema, values).find(
    (f) => isRequired(f, values) && isBlank(values[f.name])
  );
  return field ? field.name : "";
};

/**
 * Progress within a step, used by the inline CTA to show "3 of 8 completed".
 * Counts only required, visible fields — optional fields shouldn't make a
 * finished form look incomplete.
 */
export const completion = (schema, values) => {
  const required = visibleFields(schema, values).filter((f) => isRequired(f, values));
  const done = required.filter((f) => !isBlank(values[f.name])).length;
  return { done, total: required.length };
};
